import { useEffect, useState } from "react";
import useToolStore from "./toolStore";
import { getToolUsage } from "./toolApi";
import {
  ActionIcon,
  Button,
  Group,
  Table,
  Modal,
  Text,
  List,
  Alert,
  Loader,
  Stack,
} from "@mantine/core";
import { IconPencil, IconTrash, IconAlertTriangle } from "@tabler/icons-react";
import { Link } from "wouter";
import NoOverflowBadge from "../_Common/NoOverflowBadge";

export default function ToolList() {
  const { tools, fetchTools, deleteTool } = useToolStore();
  const [deleteModal, setDeleteModal] = useState(null); // { tool, usage, loading }

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const openDeleteModal = async (tool) => {
    setDeleteModal({ tool, usage: null, loading: true });
    try {
      const usage = await getToolUsage(tool.id);
      setDeleteModal({ tool, usage, loading: false });
    } catch {
      setDeleteModal({ tool, usage: [], loading: false });
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    try {
      await deleteTool(deleteModal.tool.id);
      setDeleteModal(null);
    } catch {
      // deleteTool already shows a notification on failure via the store
    }
  };

  return (
    <div>
      <Modal
        opened={deleteModal != null}
        onClose={() => setDeleteModal(null)}
        title="Delete tool"
      >
        {deleteModal && (
          <Stack>
            <Text size="sm">
              Are you sure you want to delete{" "}
              <Text span fw={700}>
                {deleteModal.tool.name}
              </Text>
              ?
            </Text>

            {deleteModal.loading && <Loader size="sm" />}

            {!deleteModal.loading && deleteModal.usage?.length > 0 && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="yellow"
                title="This tool is used by flows"
              >
                <List size="sm">
                  {deleteModal.usage.map((f) => (
                    <List.Item key={f.flowId}>
                      <Text span fw={600}>
                        {f.flowTitle || f.flowName}
                      </Text>
                      {f.isActiveVersion && " (active)"}
                      {" \u2014 "}
                      {f.versionCount} version{f.versionCount !== 1 && "s"}
                    </List.Item>
                  ))}
                </List>
                <Text size="xs" mt="xs" c="dimmed">
                  These flows will lose access to this tool after deletion.
                </Text>
              </Alert>
            )}

            {!deleteModal.loading && deleteModal.usage?.length === 0 && (
              <Text size="sm" c="dimmed">
                No flows currently reference this tool.
              </Text>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setDeleteModal(null)}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                disabled={deleteModal.loading}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Group justify="space-between">
        <h2>Tool List</h2>
        <Button component={Link} to="/tools/new" variant="outline">
          New Tool
        </Button>
      </Group>
      {tools.length === 0 ? (
        <p>No tools available.</p>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tools.map((tool) => (
              <Table.Tr key={tool.id}>
                <Table.Td>{tool.name}</Table.Td>
                <Table.Td>
                  <NoOverflowBadge
                    variant="light"
                    color={
                      tool.toolType === "agent"
                        ? "grape"
                        : tool.toolType === "virtual"
                          ? "blue"
                          : tool.toolType === "mcp"
                            ? "teal"
                            : "gray"
                    }
                  >
                    {tool.toolType || "External"}
                  </NoOverflowBadge>
                </Table.Td>
                <Table.Td>{tool.description}</Table.Td>
                <Table.Td>
                  <Group wrap="nowrap" gap="xs">
                    <ActionIcon
                      variant="default"
                      component={Link}
                      to={`/tools/${tool.id}`}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => openDeleteModal(tool)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </div>
  );
}
